/** @typedef {import('components/files/upload/types').IFile} IFile */
/** @typedef {import('components/files/upload/types').IFileError} IFileError */
/** @typedef {import('components/files/upload/types').IFileRejectionReason} IFileRejectionReason */

import { Stack, Text } from '@mantine/core';
import matchFileType from 'components/files/upload/validation/match-file-type';
import { nanoid } from 'nanoid';
import { FILE_UPLOAD_MAX_SIZE } from 'env';
import { noop } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import getErrorMessage from 'components/files/upload/validation/get-error-message';
import { IconFileUpload } from '@tabler/icons-react';
import type { IFile, IFileError, IFileRejectionReason } from 'components/files/upload/types';
import Toast from 'components/Toast';
import Button from 'components/Button';
import P3Medium from 'components/typography/P3Medium';

/**
 * Used to collect dropped files.
 */
export default function DropFiles({
  onAccept = noop,
  onReject = noop,
  accept = ['*'],
  maxFiles = Infinity,
  maxSize = FILE_UPLOAD_MAX_SIZE,
  disabled = false,
  error = '',
  withErrorList = false,
  initialErrors = [],
}: {
  onAccept?: (files: IFile[]) => void;
  onReject?: (errors: IFileError[]) => void;
  accept?: string[];
  maxFiles?: number;
  maxSize?: number;
  disabled?: boolean;
  error?: React.ReactNode;
  withErrorList?: boolean;
  initialErrors?: IFileError[];
}) {
  const [errors, setErrors] = useState<IFileError[]>(initialErrors);
  const inputId = useMemo(() => `drop-files-native-input-${nanoid()}`, []);

  /** Handles file selection. */
  const onDropImpl = (droppedFiles: File[] | null) => {
    if (!droppedFiles) {
      /* istanbul ignore next -- explicitly authorized unreachable coverage blocker */
      return; // User clicked on the "Cancel" button provided by the browser.
    }

    /** @type {IFile[]}      */ const valid: IFile[] = [];
    /** @type {IFileError[]} */ const errors: IFileError[] = [];

    for (const file of droppedFiles) {
      let errorReason;

      if (!matchFileType(file.type, accept)) {
        errorReason = 'accept';
      } else if (file.size > maxSize) {
        errorReason = 'size';
      } else if (valid.length >= (maxFiles as number)) {
        errorReason = 'maxFiles';
      } else {
        errorReason = null;
      }

      if (errorReason) {
        errors.push({ errorId: nanoid(), fileName: file.name, reason: errorReason as IFileRejectionReason });
      } else {
        valid.push({ uuid: nanoid(), file });
      }
    }

    onAccept(valid);
    onReject(errors);

    if (withErrorList) {
      setErrors((curr: IFileError[]) => [...curr, ...errors]);
    }
  };

  /** Handles native file input change. */
  const onNativeInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      onDropImpl(Array.from(files));
    }
  };

  /** Handles drag over event for native fallback. */
  const onDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    if (disabled) return;
    event.preventDefault();
    event.stopPropagation();
  };

  /** Handles drag leave event for native fallback. */
  const onDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  /** Handles drop event for native fallback. */
  const onDrop = (event: React.DragEvent<HTMLDivElement>) => {
    if (disabled) return;
    event.preventDefault();
    event.stopPropagation();

    const files = event.dataTransfer.files;
    if (files && files.length > 0) {
      onDropImpl(Array.from(files));
    }
  };

  /** Closes the specified error. */
  const closeError: (error: IFileError) => void = useCallback(
    ({ errorId }: IFileError) => setErrors((errors) => errors.filter((error) => error.errorId !== errorId)),
    [setErrors]
  );

  const hasError = error !== undefined && error !== null && error !== '';
  const border = hasError ? 'border-complementary-danger' : 'border-neutral-300';

  return (
    <>
      <div
        className={`flex justify-start border border-dashed ${border} p-4 transition-colors`}
        style={{ borderColor: hasError ? 'var(--mantine-color-red-8)' : undefined }}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        <label htmlFor={inputId} className="w-full cursor-pointer">
          <Stack align="center" spacing={16}>
            <Button
              variant="secondary"
              disabled={disabled}
              leftIcon={<IconFileUpload size="1.2rem" />}
              radius={10}
              component="span"
            >
              {'Nahrať súbory'}
            </Button>
            <Text fz={12} lh={16 / 12} color={disabled ? 'neutral300' : 'neutral700'}>
              Pretiahnite sem súbory alebo kliknite pre nahratie súborov
            </Text>
          </Stack>
          <input
            type="file"
            id={inputId}
            multiple={maxFiles > 1}
            disabled={disabled}
            onChange={onNativeInputChange}
            className="hidden"
          />
        </label>
      </div>
      {hasError && (
        <P3Medium mt={-12} color="red.8">
          {error}
        </P3Medium>
      )}

      {(withErrorList ? errors : []).map((error) => (
        <Toast
          key={error.errorId}
          type="fail"
          fullWidth
          withCloseButton
          message={getErrorMessage(error)}
          onClose={() => closeError(error)}
        />
      ))}
    </>
  );
}
